import { FieldKey } from '../context/field-keys.js';
const K = FieldKey;
/**
 * The built-in propagation map — every pair/group/composition the two legacy paths implement
 * today, reconciled per doc §1–§2. Sources (marketiq-frontend unless noted):
 *
 *  - F2 lease pairs (5):      src/app/records/controls/common/listeners/lease/auto-calculation-rent-rates-uom.ts
 *  - F3 property pairs (6):   .../listeners/property/uom-listener.ts
 *  - availability pairs (2)
 *    + F4 groups (4-way asking-rent incl. value→UOM reverse lookup; 3-way service-charge):
 *                             .../listeners/availability/availability-uom-listener.ts
 *  - F5 sale guarded pairs (2): .../listeners/sale/sale-auto-calc-listeners.ts L344–381 (passing↔vacant),
 *                             L399–439 (actual↔buyer-cost) — `mirrorRequiresBothValues: true` kept (Q2)
 *  - R5 compositions (5):     same file — SA2-fwd L709, SA3-rev L677–679, SA6 L826–839,
 *                             SA4 L242–283 + L747–771, SA7 L849–886
 *  - R1/R2 value pairing:     marketiq-bulkedit app/model/bulk-edit/calculations/index.js
 *                             VALUE_UOM_COLUMNS_MAP + .../transaction/common/commonSection.js L365–393
 *  - F6 auto-select/default:  .../listeners/property/property-type/total-availability-space.service.ts L51–64
 *                             (property totals; space HK-retail keys stay TODO(phase2), doc §3 note b)
 *
 * NOT in the map (host-owned, doc §5): the one-shot init `AskingRentUOM → AskingRentYearlyUOM
 * when blank` (availability-uom-listener.ts L112, invocation timing), the SA1/SA2-rev/SA3-fwd
 * GBP/EUR `.match` whole-currency branches (a currency-derivation write, not a compose), and
 * all write semantics (emitEvent/dirty/row headers).
 */
export const builtInUomPropagationMap = [
// ── F2 lease pairs — base-rent star of 3 pairs centred on BaseRentYearlyUom ──
{
  kind: 'pair',
  a: K.BaseRentYearlyUom,
  b: K.BaseRentMonthlyUom,
  values: [K.BaseRentYearly, K.BaseRentMonthly]
}, {
  kind: 'pair',
  a: K.BaseRentYearlyUom,
  b: K.RentPerSqmUom,
  values: [K.BaseRentYearly, K.RentPerSqm]
}, {
  kind: 'pair',
  a: K.BaseRentYearlyUom,
  b: K.LeaseArrangementTotalValueUom,
  values: [K.BaseRentYearly, K.LeaseArrangementTotalValue]
}, {
  kind: 'pair',
  a: K.EffectiveRentYearlyUom,
  b: K.EffectiveRentMonthlyUom,
  values: [K.EffectiveRentYearly, K.EffectiveRentMonthly]
}, {
  kind: 'pair',
  a: K.AskingRentYearlyUom,
  b: K.AskingRentMonthlyUom,
  values: [K.AskingRentYearly, K.AskingRentMonthly]
},
// ── F3 property pairs — min/max/avg × direct/sublease, yearly↔monthly ────────
{
  kind: 'pair',
  a: K.PropMinDirectAskingRateYearlyUom,
  b: K.PropMinDirectAskingRateMonthlyUom,
  values: [K.PropMinDirectAskingRateYearly, K.PropMinDirectAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.PropMaxDirectAskingRateYearlyUom,
  b: K.PropMaxDirectAskingRateMonthlyUom,
  values: [K.PropMaxDirectAskingRateYearly, K.PropMaxDirectAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.PropMinSubleaseAskingRateYearlyUom,
  b: K.PropMinSubleaseAskingRateMonthlyUom,
  values: [K.PropMinSubleaseAskingRateYearly, K.PropMinSubleaseAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.PropMaxSubleaseAskingRateYearlyUom,
  b: K.PropMaxSubleaseAskingRateMonthlyUom,
  values: [K.PropMaxSubleaseAskingRateYearly, K.PropMaxSubleaseAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.PropAvgDirectAskingRateYearlyUom,
  b: K.PropAvgDirectAskingRateMonthlyUom,
  values: [K.PropAvgDirectAskingRateYearly, K.PropAvgDirectAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.PropAvgSubleaseAskingRateYearlyUom,
  b: K.PropAvgSubleaseAskingRateMonthlyUom,
  values: [K.PropAvgSubleaseAskingRateYearly, K.PropAvgSubleaseAskingRateMonthly]
},
// ── availability min/max pairs (availability-uom-listener.ts L8–28; also bulkedit
//    AUTO_CALCULATION_COLUMNS Min/MaxAskingRate groups) ────────────────────────
{
  kind: 'pair',
  a: K.AvailMinAskingRateYearlyUom,
  b: K.AvailMinAskingRateMonthlyUom,
  values: [K.AvailMinAskingRateYearly, K.AvailMinAskingRateMonthly]
}, {
  kind: 'pair',
  a: K.AvailMaxAskingRateYearlyUom,
  b: K.AvailMaxAskingRateMonthlyUom,
  values: [K.AvailMaxAskingRateYearly, K.AvailMaxAskingRateMonthly]
},
// ── F5 sale guarded pairs — mirrorRequiresBothValues KEPT (sign-off Q2) ──────
{
  kind: 'pair',
  a: K.PassingRentAmountYearlyUom,
  b: K.VacantEstimatedRentalAmountUom,
  values: [K.PassingRentAmountYearly, K.VacantEstimatedRentalAmount],
  mirrorRequiresBothValues: true
}, {
  kind: 'pair',
  a: K.SalePriceActualUom,
  b: K.BuyerCostUom,
  values: [K.SalePriceActual, K.BuyerCost],
  mirrorRequiresBothValues: true
},
// ── F4 availability n-way groups ──────────────────────────────────────────────
{
  kind: 'group',
  members: [K.AvailAskingRentUom, K.AvailAskingRentYearlyTotalUom, K.AvailTotalRentEurosPerMonthUom, K.AvailAskingRentalRateMonthlyUom],
  valueOwners: {
    [K.AvailAskingRent]: K.AvailAskingRentUom,
    [K.AvailAskingRentYearlyTotal]: K.AvailAskingRentYearlyTotalUom,
    [K.AvailTotalRentEurosPerMonth]: K.AvailTotalRentEurosPerMonthUom,
    [K.AvailAskingRentalRateMonthly]: K.AvailAskingRentalRateMonthlyUom
  }
}, {
  kind: 'group',
  // Service-charge 3-way: UOM changes only in the legacy listener (L114–130) — no valueOwners.
  members: [K.AvailServiceChargeUom, K.AvailServiceChargePerMonthUom, K.AvailServiceChargePerYearUom]
},
// ── R5 sale compositions (taking over the T2.5 host-owned UOM writes) ─────────
// SA2-fwd: SalePricePerAreaUOM := CurrencyAreaMapping[SalePriceUOM][GrossAreaUOM] (L709)
{
  kind: 'compose',
  out: K.SalePricePerAreaUom,
  trigger: K.SalePricePerArea,
  currencyUom: K.SalePriceUom,
  areaUom: K.SaleGrossAreaUom
},
// SA3-rev: CurrentRentPerAnnumPerAreaUOM := CurrencyAreaMapping[AnnumUOM][GrossAreaUOM] (L677–679)
{
  kind: 'compose',
  out: K.CurrentRentPerAnnumPerAreaUom,
  trigger: K.CurrentRentPerAnnumPerArea,
  currencyUom: K.CurrentRentPerAnnumUom,
  areaUom: K.SaleGrossAreaUom
},
// SA6/VPVR: rate UOM := CurrencyAreaMapping[AmountUOM]['Square Meter'] || `${AmountUOM}/SqM`;
// legacy also nulls the rate UOM whenever it nulls the rate (L795–799, L816–819, L826–839).
{
  kind: 'compose',
  out: K.VacantPossessionValueRateUom,
  trigger: K.VacantPossessionValueRate,
  currencyUom: K.VacantPossessionValueAmountUom,
  areaLiteral: 'Square Meter',
  sqmFallback: true,
  onTriggerNull: 'clear'
},
// SA4: per-sqft UOM := the preferred (Actual-over-Reported) price's own UOM (L267, L769)
{
  kind: 'compose',
  out: K.PricePerSqFtUom,
  trigger: K.PricePerSqFt,
  preferredOf: [{
    value: K.SalePriceActual,
    uom: K.SalePriceActualUom
  }, {
    value: K.SalePrice,
    uom: K.SalePriceUom
  }]
},
// SA7: PGI-per-area UOM := NRA UOM, verbatim (L884); nulls alongside the value (L866, L873)
{
  kind: 'compose',
  out: K.SalePgiPerAreaUom,
  trigger: K.SalePgiPerArea,
  copyOf: K.NetRentableAreaUom,
  onTriggerNull: 'clear'
},
// ── F6 auto-select-first / country-default — property totals (doc §3; the T2.6
//    vacant-available fourth binder group included). Fires only when the host
//    supplies `ctx.optionsFor` (R6) / `ctx.defaultUomFor` (R7): rollout is host
//    wiring, sign-off Q4. ──────────────────────────────────────────────────────
{
  kind: 'auto-select-first',
  value: K.TotalAvailableSpace,
  uom: K.TotalAvailableSpaceUom
}, {
  kind: 'auto-select-first',
  value: K.TotalVacantSpace,
  uom: K.TotalVacantSpaceUom
}, {
  kind: 'auto-select-first',
  value: K.TotalNotVacantSpace,
  uom: K.TotalNotVacantSpaceUom
}, {
  kind: 'auto-select-first',
  value: K.TotalVacantAvailableSpace,
  uom: K.TotalVacantAvailableSpaceUom
}];
